import { useMemo } from 'react';
import { type CanvasMode, type CanvasModeConfig, CANVAS_MODE_CONFIGS } from '../types/canvasMode';

/**
 * Derives canvas configuration from the operational mode.
 *
 * This hook is the single source of truth for canvas behavior configuration.
 * All components should use this hook to determine their behavior based on
 * the canvas mode, rather than checking mode directly or passing isReadOnly
 * through multiple layers.
 *
 * @param mode - The canvas operational mode ('readonly' | 'testing' | 'editing')
 * @returns Configuration object with all canvas behavior flags
 *
 * @example
 * ```typescript
 * function AutomationFlowCanvas({ mode }: { mode: CanvasMode }) {
 *   const config = useCanvasMode(mode);
 *
 *   return (
 *     <ReactFlow
 *       nodesDraggable={config.nodesDraggable}
 *       nodesConnectable={config.nodesConnectable}
 *       elementsSelectable={config.elementsSelectable}
 *       deleteKeyCode={config.deleteKeyCode}
 *     >
 *       {config.showToolbar && <CanvasControls />}
 *       {config.showNodePalette && <NodePalette />}
 *       {config.showChainTerminusButtons && <ChainTerminusButtons />}
 *     </ReactFlow>
 *   );
 * }
 * ```
 */
export function useCanvasMode(mode: CanvasMode): CanvasModeConfig {
	return useMemo(() => CANVAS_MODE_CONFIGS[mode], [mode]);
}
